// Manual de Usuario — versión estructurada para render en el panel
// lateral del Asistente. Es una réplica del .docx del Manual de
// Usuario, adaptada a estructura navegable con capítulos → secciones.
//
// Cada capítulo tiene:
//   id, title, blocks[]
//
// Cada block es uno de:
//   { type: 'p',        text }              → párrafo
//   { type: 'h2',       text }              → subtítulo
//   { type: 'bullet',   items: [] }         → lista con viñetas
//   { type: 'numbered', items: [] }         → lista numerada (pasos)
//   { type: 'nota',     text }              → callout informativo
//   { type: 'warn',     text }              → callout de advertencia
//   { type: 'table',    headers, rows }     → tabla de referencia

const MANUAL = [
  {
    id: '1-bienvenida',
    title: '1. Bienvenida',
    blocks: [
      { type: 'p', text: 'Bienvenido a Comercio Colaborativo, el periódico digital del ecosistema Ecolab. Este manual te acompañará en el uso del panel de administración para publicar contenido, gestionar el equipo editorial y organizar las ediciones del periódico.' },
      { type: 'p', text: 'Está pensado para quienes participan en la producción editorial: editores, redactores y colaboradores.' },
      { type: 'h2', text: '1.1 ¿Qué es Comercio Colaborativo?' },
      { type: 'p', text: 'Es un periódico digital multi-regional. Un mismo sistema permite tener una edición Nacional y múltiples ediciones por departamento del país. Cuando un lector entra al sitio, se le muestra automáticamente el contenido más relevante para su ubicación.' },
      { type: 'h2', text: '1.2 ¿Cómo se relaciona con Ecolab?' },
      { type: 'p', text: 'Comercio Colaborativo forma parte del ecosistema Ecolab. Tu cuenta de usuario es la misma para todas las aplicaciones del ecosistema. No necesitas registrarte en cada app: te registras una sola vez en Ecolab y desde ahí puedes usar todas las apps a las que te den acceso.' },
    ],
  },
  {
    id: '2-iniciar-sesion',
    title: '2. Iniciar sesión',
    blocks: [
      { type: 'h2', text: '2.1 Cómo entrar al panel' },
      { type: 'numbered', items: [
        'Abre el sitio del periódico: news.fenagro-oficial.org',
        'Haz clic en "Iniciar sesión" en la barra superior.',
        'Escribe tu correo electrónico y contraseña (los mismos del ecosistema Ecolab).',
        'Al ingresar correctamente, el enlace "Panel de administración" aparecerá disponible en el menú si tu cuenta tiene un rol interno asignado.',
      ]},
      { type: 'h2', text: '2.2 "Sin acceso al panel"' },
      { type: 'p', text: 'Si al intentar entrar al panel ves un mensaje "Sin acceso al panel", significa que tu cuenta está autenticada pero aún no tiene un rol interno asignado. Contacta al EditorNacional o a un Editor para que te asigne el rol correspondiente.' },
      { type: 'nota', text: 'Hay dos niveles: primero Ecolab autoriza tu cuenta para usar la aplicación News (lo hace un admin de Ecolab). Segundo, dentro de News se te asigna un rol interno (lo hace un Editor). Ambos son necesarios.' },
    ],
  },
  {
    id: '3-roles',
    title: '3. Roles y permisos',
    blocks: [
      { type: 'p', text: 'El sistema tiene cinco roles internos con permisos progresivos. Un usuario puede tener uno o varios roles simultáneamente.' },
      { type: 'h2', text: '3.1 Descripción de cada rol' },
      { type: 'table',
        headers: ['Rol', 'Qué puede hacer'],
        rows: [
          ['EditorNacional', 'Máximo nivel. Opera sobre todo el país sin restricción de departamento. Único que puede crear o modificar la edición Nacional. Único que asigna roles a otros usuarios y gestiona los departamentos asignados.'],
          ['Editor',         'Publica artículos, aprueba/rechaza revisiones y gestiona autores. Solo puede tocar ediciones de los departamentos asignados. NO puede tocar la edición Nacional.'],
          ['Redactor',       'Escribe y edita artículos. Puede enviar artículos a revisión. Si el periódico requiere aprobación, no publica directamente.'],
          ['Colaborador',    'Escribe artículos (siempre en borrador). Solo puede editar sus propios artículos.'],
          ['SoloLectura',    'Puede ver el panel pero no modifica nada.'],
        ],
      },
      { type: 'h2', text: '3.2 Escalera de asignación' },
      { type: 'p', text: 'Solo los usuarios con rol EditorNacional o Editor pueden asignar roles a otros.' },
      { type: 'bullet', items: [
        'El EditorNacional puede asignar cualquier rol, incluidos otros EditorNacional y Editor.',
        'El Editor puede asignar Redactor, Colaborador y SoloLectura. No puede asignar Editor ni EditorNacional.',
        'Nadie puede modificar sus propios roles.',
      ]},
    ],
  },
  {
    id: '4-panel-admin',
    title: '4. Panel de administración',
    blocks: [
      { type: 'p', text: 'El panel es el espacio donde el equipo editorial gestiona el contenido. Se accede desde el enlace "Panel de administración" en la barra superior una vez que has iniciado sesión con un rol interno.' },
      { type: 'h2', text: '4.1 Estructura del panel' },
      { type: 'p', text: 'El panel se organiza en un menú lateral con pestañas. Las pestañas que ves dependen de tu rol:' },
      { type: 'table',
        headers: ['Pestaña', 'Roles que la ven'],
        rows: [
          ['Dashboard',              'Editor, Redactor, EditorNacional'],
          ['Artículos',              'Todos'],
          ['Categorías',             'Editor, EditorNacional'],
          ['Ediciones',              'Editor, EditorNacional'],
          ['Autores',                'Editor, Redactor, EditorNacional'],
          ['Usuarios y roles',       'Editor, EditorNacional'],
          ['Periódico',              'Editor, EditorNacional'],
          ['Parámetros editoriales', 'Editor, EditorNacional'],
          ['Revisiones pendientes',  'Editor, EditorNacional'],
          ['Asistente',              'Todos (chatbot de ayuda)'],
          ['Ayuda y reglas',         'Todos'],
        ],
      },
      { type: 'h2', text: '4.2 Bajo tu nombre' },
      { type: 'p', text: 'En la parte superior del menú lateral verás tu nombre y, debajo, un chip dorado con tu rol actual. Si tienes varios roles se muestran varios chips.' },
    ],
  },
  {
    id: '5-crear-articulo',
    title: '5. Cómo crear un artículo',
    blocks: [
      { type: 'p', text: 'Los artículos son la unidad principal de contenido. Cualquier persona con rol Colaborador o superior puede crear artículos.' },
      { type: 'h2', text: '5.1 Crear un artículo nuevo' },
      { type: 'numbered', items: [
        'Ve a la pestaña Artículos.',
        'Haz clic en "Nuevo artículo".',
        'Escribe el título.',
        'Escribe un extracto (resumen que aparecerá en las cards).',
        'Escribe el contenido en el editor enriquecido.',
        'Selecciona una categoría en el panel derecho.',
        'Selecciona uno o varios autores.',
        'Selecciona el estado (las opciones dependen de tu rol).',
        'Sube o pega la URL de la imagen principal.',
        'Haz clic en Guardar.',
      ]},
      { type: 'h2', text: '5.2 Estados de un artículo' },
      { type: 'table',
        headers: ['Estado', 'Descripción'],
        rows: [
          ['Borrador',    'No es visible en el sitio público. Es el estado inicial.'],
          ['En revisión', 'Enviado a un Editor para aprobar. Aún no público.'],
          ['Publicado',   'Visible en el sitio público. Aparece en listados y ediciones.'],
          ['Archivado',   'Retirado del sitio. Solo Editor y EditorNacional pueden archivar.'],
        ],
      },
      { type: 'h2', text: '5.3 Reglas por rol' },
      { type: 'bullet', items: [
        'Colaborador: solo Borrador. No puede marcar destacado ni Última hora. Solo edita los suyos.',
        'Redactor: Borrador, En revisión, Publicado (si el periódico no exige aprobación). Puede marcar destacado y Última hora. Edita artículos de cualquiera.',
        'Editor y EditorNacional: los cuatro estados incluido Archivado. Todos los flags. Editan cualquier artículo.',
      ]},
      { type: 'nota', text: 'Si el Colaborador termina su artículo, guárdalo en Borrador. Un Editor o Redactor lo revisará y lo pasará a "Publicado" cuando esté listo.' },
    ],
  },
  {
    id: '6-ediciones',
    title: '6. Ediciones',
    blocks: [
      { type: 'p', text: 'Una edición es un compilado especial del periódico: un número identificado con fecha, portada, opcionalmente PDF y un conjunto de artículos vinculados.' },
      { type: 'h2', text: '6.1 Edición Nacional vs regional' },
      { type: 'p', text: 'Cada edición está asociada a un departamento (por su municipio) o marcada como Nacional. Solo puede haber una edición Nacional publicada al mismo tiempo por periódico.' },
      { type: 'bullet', items: [
        'Edición Nacional: se muestra por defecto a cualquier lector que no tenga edición regional para su ubicación. Solo el EditorNacional la gestiona.',
        'Edición regional: se muestra a los lectores del departamento correspondiente. Cualquier Editor con ese departamento asignado (o el EditorNacional) puede gestionarla.',
      ]},
      { type: 'h2', text: '6.2 Crear una edición' },
      { type: 'numbered', items: [
        'Ve a la pestaña Ediciones.',
        'Haz clic en "Nueva edición".',
        'Escribe el Número y la Fecha editorial.',
        'Opcionalmente escribe un Título.',
        'Selecciona un Municipio (el sistema resuelve el departamento).',
        'Si eres EditorNacional y esta debe ser la Nacional, marca la casilla correspondiente (al hacerlo el municipio se ignora).',
        'Sube la portada y opcionalmente un PDF.',
        'En la sección Artículos, selecciona los artículos que quieres incluir.',
        'Cambia el estado a Publicado y haz clic en Guardar.',
      ]},
      { type: 'warn', text: 'Si dejas el estado en Borrador, la edición no será visible en el sitio público, aunque tenga artículos y portada.' },
      { type: 'h2', text: '6.3 Cómo el lector ve las ediciones' },
      { type: 'bullet', items: [
        'El sistema detecta el departamento del lector (por geolocalización o por elección manual desde el selector superior).',
        'Busca la última edición publicada para ese departamento.',
        'Si no existe, muestra la edición Nacional con aviso: "Tu región aún no tiene edición propia".',
        'Si tampoco hay Nacional, muestra un mensaje editorial: "No hay edición publicada para esta región".',
      ]},
      { type: 'h2', text: '6.4 La home muestra solo artículos de la edición vigente' },
      { type: 'p', text: 'La página principal no muestra todos los artículos publicados: muestra únicamente los vinculados a la edición vigente del departamento del lector. Esto le da al equipo editorial control sobre qué aparece en la portada.' },
      { type: 'p', text: 'Un artículo publicado pero NO vinculado a ninguna edición vigente sigue siendo accesible por su categoría y en el perfil de sus autores, pero no en la home.' },
    ],
  },
  {
    id: '7-autores',
    title: '7. Autores',
    blocks: [
      { type: 'p', text: 'Los autores son las personas que firman los artículos. Pueden estar vinculados a un usuario del sistema o ser standalone (colaborador invitado sin cuenta).' },
      { type: 'h2', text: '7.1 Autor automático al asignar rol' },
      { type: 'p', text: 'Cuando un Editor asigna un rol de escritura a un usuario, el sistema crea automáticamente un Autor asociado a ese usuario. El autor auto-creado no tiene bio, foto ni cargo — se completan después.' },
      { type: 'h2', text: '7.2 Crear un autor standalone' },
      { type: 'numbered', items: [
        'Ve a la pestaña Autores.',
        'Haz clic en "Nuevo autor".',
        'Completa nombre, cargo, bio y foto.',
        'Guarda. Este autor podrá ser seleccionado en cualquier artículo.',
      ]},
    ],
  },
  {
    id: '8-usuarios-roles',
    title: '8. Gestión de usuarios y roles',
    blocks: [
      { type: 'p', text: 'Esta sección aplica solo a Editores y EditorNacional. La pestaña Usuarios y roles muestra a todos los usuarios que han iniciado sesión al menos una vez en el periódico.' },
      { type: 'h2', text: '8.1 De dónde vienen los usuarios' },
      { type: 'p', text: 'Los usuarios NO se crean desde el panel de News. La identidad es responsabilidad de Ecolab. El flujo es:' },
      { type: 'numbered', items: [
        'El administrador de Ecolab autoriza a la persona para usar la aplicación News.',
        'La persona hace login por primera vez en el sitio.',
        'Aparece en el listado de Usuarios y roles con la etiqueta "Sin rol".',
        'El Editor le asigna uno o varios roles internos.',
      ]},
      { type: 'h2', text: '8.2 Asignar un rol' },
      { type: 'numbered', items: [
        'Ve a la pestaña Usuarios y roles.',
        'Busca al usuario por nombre o correo.',
        'Haz clic en "Asignar rol" (o el icono de editar si ya tiene roles).',
        'En el modal, selecciona el rol y haz clic en "Asignar rol".',
        'Si es un rol de escritura, el sistema creará automáticamente un Autor asociado.',
      ]},
      { type: 'h2', text: '8.3 Revocar un rol' },
      { type: 'p', text: 'Abre el modal del usuario. En "Roles activos", haz clic en "Revocar" al lado del rol que quieres quitar. La revocación no borra los artículos escritos por esa persona.' },
      { type: 'h2', text: '8.4 Asignar departamentos (scope regional)' },
      { type: 'bullet', items: [
        'Un Editor solo verá y podrá tocar ediciones de sus departamentos asignados.',
        'Un Editor sin departamentos asignados no verá ninguna edición.',
        'Para Redactores y Colaboradores el scope regional es de agrupación de equipo.',
        'EditorNacional ignora esta sección: siempre opera sobre todo.',
      ]},
      { type: 'numbered', items: [
        'Abre el modal del usuario.',
        'En "Departamentos asignados", elige un departamento del desplegable.',
        'Haz clic en "Asignar".',
        'Para quitar uno, haz clic en "Quitar" al lado del departamento.',
      ]},
    ],
  },
  {
    id: '9-parametros-editoriales',
    title: '9. Parámetros editoriales',
    blocks: [
      { type: 'h2', text: '9.1 Requiere aprobación' },
      { type: 'p', text: 'Si está activo, los Redactores no publican directamente: sus artículos entran en "En revisión" y esperan aprobación de un Editor. Los Editores siempre publican directamente.' },
      { type: 'h2', text: '9.2 Modo mantenimiento' },
      { type: 'p', text: 'Si está activo, el sitio público muestra un mensaje de mantenimiento. El panel admin sigue funcionando normalmente. Úsalo para trabajos que puedan dejar el sitio inconsistente.' },
      { type: 'h2', text: '9.3 Director del periódico' },
      { type: 'p', text: 'Selecciona a la persona que aparecerá como Director en el pie del sitio (foto, nombre, cargo). Debe existir como Autor en el sistema.' },
    ],
  },
  {
    id: '10-revisiones',
    title: '10. Revisiones pendientes',
    blocks: [
      { type: 'p', text: 'Cuando un Redactor guarda un artículo en "En revisión", aparece en Revisiones pendientes (visible solo para Editores y EditorNacional).' },
      { type: 'h2', text: '10.1 Revisar un artículo' },
      { type: 'numbered', items: [
        'Ve a la pestaña Revisiones pendientes.',
        'Haz clic en el artículo para leerlo.',
        'Si está listo, cámbialo a estado "Publicado".',
        'Si necesita cambios, devuélvelo a "Borrador" y comunícate con el Redactor.',
      ]},
    ],
  },
  {
    id: '11-config-visual',
    title: '11. Configuración visual del periódico',
    blocks: [
      { type: 'p', text: 'En la pestaña Periódico (Editor y EditorNacional) se configuran los datos visuales del sitio.' },
      { type: 'h2', text: '11.1 Nombre y tagline' },
      { type: 'p', text: 'El nombre aparece grande en la cabecera. El tagline es la frase pequeña debajo.' },
      { type: 'h2', text: '11.2 Colores' },
      { type: 'p', text: 'Puedes elegir dos colores: primario (fondo de barras y elementos destacados) y secundario (dorado por defecto, para acentos). Cada campo tiene selector visual y admite código hexadecimal directo.' },
    ],
  },
  {
    id: '12-asistente',
    title: '12. Asistente virtual',
    blocks: [
      { type: 'p', text: 'La pestaña Asistente contiene un chatbot que responde consultas sobre el uso del sistema. Puedes preguntar sobre roles, cómo publicar, ediciones regionales, cómo asignar departamentos, etc.' },
      { type: 'p', text: 'El asistente busca en su base de conocimiento y devuelve la respuesta más relevante. Si no encuentra algo específico, sugiere temas relacionados. En futuras versiones se conectará con un motor RAG para respuestas más ricas.' },
    ],
  },
  {
    id: '13-flags',
    title: '13. Clasificación de artículos por flags',
    blocks: [
      { type: 'p', text: 'Además del estado (borrador, en revisión, publicado, archivado), los artículos y sus vínculos con ediciones tienen banderas que controlan dónde y cómo aparecen en el sitio.' },
      { type: 'h2', text: '13.1 Los cuatro flags disponibles' },
      { type: 'table',
        headers: ['Flag', 'Alcance', 'Dónde aparece', 'Quién puede'],
        rows: [
          ['Destacado',    'Global (por artículo)',  'Bloque de destacados del sitio.',                                   'Editor, Redactor, EditorNacional'],
          ['Última hora',  'Global (por artículo)',  'Barra amarilla superior de la home.',                               'Editor, Redactor, EditorNacional'],
          ['Trending',     'Global (por artículo)',  'Bloque "Lo más leído".',                                            'Editor, Redactor, EditorNacional'],
          ['Portada',      'Por edición (vínculo)',  'Hero grande de la home cuando esa edición está vigente.',           'Editor (en su scope), EditorNacional'],
        ],
      },
      { type: 'h2', text: '14.2 Diferencia entre global y por edición' },
      { type: 'p', text: 'Los tres primeros flags viven en el artículo: aunque no vincules el artículo a ninguna edición, marcado como "última hora" sigue apareciendo en la barra amarilla. Portada es distinto: vive en el vínculo. El mismo artículo puede ser portada en la Nacional y no serlo en la regional de Antioquia (o al revés).' },
      { type: 'h2', text: '14.3 Solo una portada por edición' },
      { type: 'p', text: 'Al marcar un nuevo artículo como portada desde el editor, el sistema desmarca automáticamente el que estuviera antes. La base de datos también valida esta regla como última defensa: es imposible tener dos portadas por edición.' },
      { type: 'nota', text: 'Si no marcas ningún artículo como portada, la home usa el primer artículo del orden manual como hero. Marcar una portada explícita sobrescribe el orden manual solo para el hero.' },
      { type: 'h2', text: '14.4 Cómo marcar la portada' },
      { type: 'numbered', items: [
        'Abre el editor de la edición.',
        'En la columna derecha "En esta edición", ubica el artículo que quieres como hero.',
        'Haz clic en el botón de estrella al lado del botón X. La estrella se rellena en dorado.',
        'El artículo muestra un chip dorado "PORTADA" al lado del título.',
        'Guarda la edición.',
      ]},
      { type: 'p', text: 'Para quitar la portada sin poner otra: haz clic otra vez en la estrella activa. Para cambiarla: haz clic en la estrella de otro artículo (el anterior se desmarca solo).' },
    ],
  },
  {
    id: '14-faq',
    title: '14. Preguntas frecuentes',
    blocks: [
      { type: 'h2', text: '14.1 Inicié sesión pero no veo el panel' },
      { type: 'p', text: 'Tu cuenta está autenticada en Ecolab, pero aún no tiene un rol interno asignado en News. Pídele a un Editor que te asigne uno.' },
      { type: 'h2', text: '14.2 No puedo cambiar mi propio rol' },
      { type: 'p', text: 'Es intencional. Nadie puede modificar sus propios roles. Pídele a otro Editor que lo haga.' },
      { type: 'h2', text: '14.3 Publiqué una edición pero no aparece' },
      { type: 'p', text: 'Verifica: (1) que el estado esté en Publicado, (2) que el municipio corresponda al departamento del lector, y (3) que tenga artículos vinculados. La home muestra solo artículos de la edición vigente.' },
      { type: 'h2', text: '14.4 ¿Por qué solo veo "Borrador" en el select de estado?' },
      { type: 'p', text: 'Porque tienes rol Colaborador. Este rol solo permite guardar en borrador.' },
      { type: 'h2', text: '14.5 Un Editor de otro departamento no ve mi edición' },
      { type: 'p', text: 'Correcto: cada Editor solo ve las ediciones de sus departamentos asignados. Si necesitas que la vea, pídele a un EditorNacional que le asigne ese departamento.' },
      { type: 'h2', text: '14.6 Mi foto de autor no aparece' },
      { type: 'p', text: 'Verifica en la pestaña Autores que la foto esté cargada. Refresca la página. Si persiste, contacta al Editor.' },
      { type: 'h2', text: '14.7 Cambié mi contraseña en Ecolab y ya no puedo entrar' },
      { type: 'p', text: 'Cierra sesión completamente y vuelve a entrar con la contraseña nueva. Si no funciona, prueba en incógnito para descartar caché.' },
      { type: 'h2', text: '14.8 ¿Puedo trabajar en varios periódicos?' },
      { type: 'p', text: 'Sí. El sistema es multi-tenant: un mismo usuario puede tener roles en varios periódicos.' },
    ],
  },
  {
    id: 'glosario',
    title: 'Glosario',
    blocks: [
      { type: 'table',
        headers: ['Término', 'Definición'],
        rows: [
          ['Artículo',       'Unidad de contenido publicable con título, autor, categoría, cuerpo y estado.'],
          ['Autor',          'Persona que firma un artículo. Puede estar vinculado a un usuario o ser standalone.'],
          ['Borrador',       'Estado inicial de un artículo o edición. No es visible en el sitio.'],
          ['Categoría',      'Sección temática (Economía, Salud, etc.).'],
          ['Colaborador',    'Rol que puede crear artículos siempre en borrador. Solo edita los suyos.'],
          ['Departamento',   'División administrativa del país. Usada para el scope regional.'],
          ['Ecolab',         'Sistema central del ecosistema que gestiona la identidad y autoriza el acceso a las apps.'],
          ['Edición',        'Compilado especial con portada, fecha y artículos vinculados. Regional o Nacional.'],
          ['Editor',         'Rol con permisos de publicación y gestión. Regional (por departamentos asignados).'],
          ['EditorNacional', 'Super-usuario del periódico. Sin restricción de departamento.'],
          ['Fallback',       'Comportamiento cuando no hay edición regional: sirve la Nacional al lector.'],
          ['JWT',            'Token de autenticación firmado por Ecolab que viaja en cada petición.'],
          ['Multi-tenant',   'Un mismo sistema hosteando varios periódicos independientes.'],
          ['Panel Admin',    'Área privada donde el equipo editorial trabaja.'],
          ['Redactor',       'Rol de escritura con más permisos que Colaborador.'],
          ['Rol',            'Conjunto de permisos asignados a un usuario dentro de News.'],
          ['Scope regional', 'Conjunto de departamentos en los que un usuario puede operar.'],
          ['SSO',            'Single Sign-On. Un solo login sirve para varias aplicaciones del ecosistema.'],
        ],
      },
    ],
  },
];

export default MANUAL;
